import { toast } from "@/hooks/use-toast";
import { subscriptionHttpGet } from "./subscription/subscriptionHttpGet";
import { dedupeInFlightGet } from "@/utils/inFlightGetDedup";
import { isValidUserId } from "@/utils/isValidUserId";
import {
  transformSubscriptionPlans,
  type TransformedSubscriptionPlan,
} from "./subscription/subscriptionPlanTransformHelpers";

export type Plan = TransformedSubscriptionPlan;

/**
 * Fetches and transforms subscription plans from the subscription service API.
 * @example
 * fetchSubscriptionPlans()
 * Returns an array of transformed plan objects.
 * @returns {Promise<Plan[]>} Array of transformed subscription plans objects.
 * @description
 *   - Uses subscription service API endpoint to retrieve subscription plans.
 *   - Parses the 'features' field from JSON string format into an object.
 *   - Logs helpful error messages in the console if operations fail.
 *   - Provides default values for 'features' properties if they are missing or incorrect.
 */
export const fetchSubscriptionPlans = async (): Promise<Plan[]> => {
  try {
    const { API_ENDPOINTS } = await import("@/config/api");

    const data = await subscriptionHttpGet<
      Array<{
        id: string;
        name: string;
        price: number;
        duration_days: number;
        features: string | Record<string, unknown>;
      }>
    >(`${API_ENDPOINTS.SUBSCRIPTIONS.LIST}/plans`);

    if (!data) {
      return [];
    }

    // Log raw data in dev mode only
    if (import.meta.env.DEV) {
      console.warn("Raw subscription plans data:", data);
    }

    return transformSubscriptionPlans(data);
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to fetch subscription plans";
    console.error("Error fetching subscription plans:", error);
    toast({
      title: "Failed to fetch subscription plans",
      description: errorMessage,
      variant: "destructive",
    });
    return [];
  }
};

/**
 * Fetch the most recent subscription for a given user via API.
 * @example
 * sync('user-id-123')
 * // Returns the data of the most recent subscription for the user or null if not found.
 * @param {string} userId - The unique identifier for the user whose subscription is being synced.
 * @returns {object | null} Subscription data object representing the most recent subscription, or null if an error occurs or no subscription is found.
 * @description
 *   - Uses subscription service API endpoint.
 *   - Returns null if no subscription is found (404).
 */
export const getCurrentSubscription = async (userId: string) => {
  try {
    if (!isValidUserId(userId)) {
      return null;
    }

    const { API_ENDPOINTS } = await import("@/config/api");

    return await subscriptionHttpGet(`${API_ENDPOINTS.SUBSCRIPTIONS.LIST}/current/${userId}`, {
      allow404: true,
    });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Failed to fetch subscription";
    const errorCode =
      error && typeof error === "object" && "code" in error
        ? (error as { code?: string }).code
        : undefined;
    const isRateLimited =
      errorMessage.includes("429") || errorMessage.toLowerCase().includes("too many requests");
    if (errorCode !== "PGRST116" && !isRateLimited) {
      toast({
        title: "Failed to fetch subscription",
        description: errorMessage,
        variant: "destructive",
      });
    }
    return null;
  }
};

/** Current company link for "Sponsored by X" UI */
export interface CompanyLinkCurrent {
  id: string;
  company_id: string;
  company_name: string | null;
  company_slug: string | null;
  joined_at: string | null;
}

/**
 * Validate invite code and return company name (for confirmation).
 * @param code - 8-character alphanumeric invite code
 * @returns Company name and slug or throws
 */
export const validateCompanyCode = async (
  code: string
): Promise<{ company_name: string; company_slug: string }> => {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const normalized = code
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (normalized.length !== 8) {
    throw new Error("Invalid code. Code must be 8 characters.");
  }
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_VALIDATE(normalized), {
    method: "GET",
    headers: getAuthHeader(token || undefined),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Code not found or expired.");
  }
  return { company_name: data.company_name, company_slug: data.company_slug };
};

/**
 * Link current user to company with invite code.
 * @param code - 8-character alphanumeric invite code
 * @returns Company name and slug on success
 */
export const linkWithCompany = async (
  code: string
): Promise<{ company_name: string; company_slug: string }> => {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const normalized = code
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (normalized.length !== 8) {
    throw new Error("Invalid code. Code must be 8 characters.");
  }
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK, {
    method: "POST",
    headers: getAuthHeader(token || undefined),
    body: JSON.stringify({ code: normalized }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Failed to link to company.");
  }
  return {
    company_name: data.company_name ?? "",
    company_slug: data.company_slug ?? "",
  };
};

/**
 * Unlink current user from company.
 */
export const unlinkFromCompany = async (): Promise<void> => {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_UNLINK, {
    method: "POST",
    headers: getAuthHeader(token || undefined),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Failed to unlink from company.");
  }
};

/**
 * Get current company link for user (for "Sponsored by X" UI).
 * @param userId - Current user id
 * @returns Current link or null
 */
export const getCurrentCompanyLink = async (userId: string): Promise<CompanyLinkCurrent | null> => {
  try {
    const { API_ENDPOINTS } = await import("@/config/api");
    const data = await subscriptionHttpGet<CompanyLinkCurrent>(
      API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_CURRENT(userId),
      { allow404: true }
    );
    return data;
  } catch {
    return null;
  }
};

/**
 * List companies where the current user is an HR admin.
 * @param sessionToken - Optional. Pass session.access_token to avoid timing issues right after login.
 */
export const getMyCompanies = async (
  sessionToken?: string | null
): Promise<{ slug: string; name: string }[]> => {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = sessionToken != null && sessionToken !== "" ? sessionToken : await getAuthToken();
  const endpoint = API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_MY_COMPANIES;

  const data = await dedupeInFlightGet(endpoint, async () => {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: getAuthHeader(token || undefined),
      credentials: "include",
    });
    if (!response.ok) {
      return { companies: [] as { slug: string; name: string }[] };
    }
    return response.json() as Promise<{ companies?: { slug: string; name: string }[] }>;
  });

  return data?.companies ?? [];
};

/**
 * Fetch companies with retries for login redirect. Use when session may not be ready yet.
 * Retries on empty result with increasing delays (common when JWT isn't propagated yet).
 */
export const getMyCompaniesWithRetries = async (
  sessionToken?: string | null,
  opts?: { maxRetries?: number; delays?: number[] }
): Promise<{ slug: string; name: string }[]> => {
  const maxRetries = opts?.maxRetries ?? 4;
  const delays = opts?.delays ?? [200, 350, 500, 800];
  let last: { slug: string; name: string }[] = [];
  for (let i = 0; i < maxRetries; i += 1) {
    if (i > 0) await new Promise((r) => setTimeout(r, delays[i - 1]));
    last = await getMyCompanies(sessionToken);
    if (last.length > 0) return last;
  }
  return last;
};

/**
 * Create a company (Phase 1.2). Creator becomes first HR. Company is pending until email verified.
 * @param name - Company display name
 * @param slug - 3-10 chars, lowercase, a-z0-9 and hyphen (optional; derived from name if omitted)
 */
export const createCompany = async (
  name: string,
  slug?: string
): Promise<{
  company_id: string;
  company_name: string;
  company_slug: string;
  status: string;
  trial_end?: string | null;
  message?: string;
}> => {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_CREATE, {
    method: "POST",
    headers: { ...getAuthHeader(token || undefined), "Content-Type": "application/json" },
    body: JSON.stringify({ name: name.trim(), slug: slug?.trim() || undefined }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { error?: string }).error || "Failed to create company.");
  }
  return data as {
    company_id: string;
    company_name: string;
    company_slug: string;
    status: string;
    trial_end?: string | null;
    message?: string;
  };
};

/**
 * Verify company (B2B2C). Public, no auth. Token from verification email.
 * On success company becomes active and trial starts.
 */
export const verifyCompany = async (
  token: string
): Promise<{
  success: boolean;
  company_slug: string;
  company_name: string;
}> => {
  const { API_ENDPOINTS } = await import("@/config/api");
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_VERIFY, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { error?: string }).error || "Invalid or expired verification link.");
  }
  return data as { success: boolean; company_slug: string; company_name: string };
};

/** Phase 4: Dependent row from list */
export interface CompanyLinkDependent {
  id: string;
  email: string;
  status: string;
  joined_at: string | null;
  left_at: string | null;
  created_at: string;
}

/** Phase 4: List dependents for current user's active employee link */
export async function getDependents(): Promise<CompanyLinkDependent[]> {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_DEPENDENTS, {
    method: "GET",
    headers: getAuthHeader(token || undefined),
  });
  if (!response.ok) throw new Error("Failed to fetch dependents");
  return response.json();
}

/** Phase 4: Add dependent (invite by email). Max 2 per employee. */
export async function addDependent(email: string): Promise<{
  id: string;
  email: string;
  status: string;
  code: string;
  expires_at: string;
}> {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_DEPENDENTS, {
    method: "POST",
    headers: { ...getAuthHeader(token || undefined), "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error((data as { error?: string }).error || "Failed to add dependent");
  return data as { id: string; email: string; status: string; code: string; expires_at: string };
}

/** Phase 4: Validate dependent invite code (for accept page) */
export async function validateDependentInvite(code: string): Promise<{
  company_name: string;
  company_slug: string;
  email: string;
}> {
  const { API_ENDPOINTS } = await import("@/config/api");
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_DEPENDENT_VALIDATE(code), {
    method: "GET",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error((data as { error?: string }).error || "Invalid or expired code");
  return data as { company_name: string; company_slug: string; email: string };
}

/** Phase 4: Accept dependent invite (user must be logged in; email must match) */
export async function acceptDependentInvite(code: string): Promise<{
  success: boolean;
  company_name: string;
  company_slug: string;
}> {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_DEPENDENT_ACCEPT, {
    method: "POST",
    headers: { ...getAuthHeader(token || undefined), "Content-Type": "application/json" },
    body: JSON.stringify({
      code: code
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, ""),
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok)
    throw new Error((data as { error?: string }).error || "Failed to accept invite");
  return data as { success: boolean; company_name: string; company_slug: string };
}

/** Phase 4: Remove dependent (set left_at). Employee or dependent. */
export async function removeDependent(dependentId: string): Promise<void> {
  const { API_ENDPOINTS, getAuthHeader } = await import("@/config/api");
  const { getAuthToken } = await import("./assets/assetHelpers");
  const token = await getAuthToken();
  const response = await fetch(
    API_ENDPOINTS.SUBSCRIPTIONS.COMPANY_LINK_DEPENDENT_REMOVE(dependentId),
    { method: "POST", headers: getAuthHeader(token || undefined) }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || "Failed to remove dependent");
  }
}
